import React, { useEffect, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { FaCalendarAlt, FaInfoCircle, FaSearch } from 'react-icons/fa';

import axios from 'axios';

import DatePopup from '../../components/CustomUI/DatePopup';
// Import custom CSS file
import Loader from '../../components/CustomUI/Loader';
import SymptomDetailsPopup from '../../components/CustomUI/SymptomDetailsPopup';
import { Checkbox } from '../../components/Form';
import { getServiceUrl } from '../../config';

import './Patient.css';

const apiBaseUrl = getServiceUrl();

const Symptoms = ({ patientData, patientguid, onDataUpdate }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [activeDatePopup, setActiveDatePopup] = useState(null); // State for popup visibility
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSymptom, setSelectedSymptom] = useState(null);

  const getPatientSymptoms = async () => {
    // Fetch patient's symptoms
    const patientConfig = {
      headers: {
        rdxtoken: localStorage.getItem('tokenId'),
        patientguid: patientguid,
      },
    };
    const patientResponse = await axios.get(
      apiBaseUrl + 'doctors/getpatientsymptoms',
      patientConfig,
    );
    console.log(patientResponse);
    return patientResponse.data[0]?.symptomdetails || [];
  };

  const getSymptoms = async () => {
    const config = {
      headers: {
        rdxtoken: localStorage.getItem('tokenId'),
      },
    };

    // Fetch master symptoms
    const masterResponse = await axios.get(
      apiBaseUrl + 'doctors/symptoms',
      config,
    );
    return masterResponse.data || [];
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const masterSymptoms = await getSymptoms();
        setSuggestions(masterSymptoms);

        const existingSymptoms = await getPatientSymptoms();
        // Map through existing symptoms and find matching details
        const preSelectedItems = existingSymptoms
          .map((symptom) => {
            const suggestion = masterSymptoms.find(
              (s) => s.hpoid === symptom.hpoid,
            );
            return {
              hpoid: symptom.hpoid,
              synonyms: suggestion?.synonyms || '',
              name: suggestion?.name || '',
              comment: suggestion?.comment || '',
              startDate: symptom.startdate || null,
              endDate: symptom.enddate || null,
              source: symptom.source,
              checked: true,
            };
          })
          .flat(); // Flatten the array after mapping
        setSelectedItems(preSelectedItems);
        handleDataUpdateAction(preSelectedItems);
        setLoading(false);
      } catch (error) {
        console.log('Error fetching data:', error);
      }
    };

    fetchData();
  }, [patientguid]);

  const handleDataUpdateAction = (data) => {
    onDataUpdate({ symptomsData: data });
  };

  const handleChange = (e) => {
    const userInput = e.target.value;
    setQuery(userInput);

    if (userInput.length > 0) {
      const filtered = suggestions
        .filter(
          (suggestion) =>
            (suggestion.name.toLowerCase().includes(userInput.toLowerCase()) ||
              (suggestion.synonyms &&
                suggestion.synonyms
                  .toLowerCase()
                  .includes(userInput.toLowerCase()))) &&
            !selectedItems.find((item) => item.name === suggestion.name),
        )
        .slice(0, 10);
      setFilteredSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setFilteredSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    const isItemSelected = selectedItems.find(
      (item) => item.hpoid === suggestion.hpoid,
    );

    // If the item is already selected, remove it
    if (isItemSelected) {
      setSelectedItems((prevItems) => {
        const updatedItems = prevItems.filter(
          (item) => item.hpoid !== suggestion.hpoid,
        );
        handleDataUpdateAction(updatedItems);
        return updatedItems;
      });
    }
    // If the item is not selected, add it
    else {
      setSelectedItems((prevItems) => {
        const updatedItems = [
          ...prevItems,
          {
            hpoid: suggestion.hpoid,
            synonyms: suggestion.synonyms,
            name: suggestion.name,
            comment: suggestion.comment,
            startDate: null,
            endDate: null,
            checked: true,
          },
        ];

        handleDataUpdateAction(updatedItems);

        return updatedItems;
      });
    }

    setQuery('');
    setFilteredSuggestions([]);
    setShowSuggestions(false);
  };

  function formatDate(dateString) {
    if (!dateString) return null;
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' }); // Get the full month name
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  }

  const handleTagToggle = (item) => {
    setSelectedItems((prevItems) => {
      const updatedItems = prevItems.map((i) =>
        i.hpoid === item.hpoid ? { ...i, checked: !i.checked } : i,
      );
      const checkedItems = updatedItems.filter((i) => i.checked);
      handleDataUpdateAction(checkedItems);
      return updatedItems;
    });
  };

  const handleDateChange = (startDate, endDate) => {
    if (activeDatePopup) {
      const updatedItems = selectedItems.map((item) =>
        item.hpoid === activeDatePopup.hpoid
          ? { ...item, startDate, endDate }
          : item,
      );
      setSelectedItems(updatedItems);
      handleDataUpdateAction(updatedItems);
    }
  };

  const handleCalendarClick = (item) => {
    setActiveDatePopup(item);
  };

  const handleInfoClick = (item) => {
    setSelectedSymptom(item);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedSymptom(null);
  };

  const handleCloseDatePopup = () => {
    setActiveDatePopup(null);
  };

  return loading ? (
    <Loader />
  ) : (
    <div style={{ margin: 'auto' }}>
      <div className="flex w-full flex-col gap-5">
        <div style={{ position: 'relative' }}>
          <div className="relative flex-1">
            <input
              type="text"
              value={query}
              onChange={handleChange}
              placeholder="Type to search..."
              className="w-full rounded border py-2 pl-10 pr-4"
            />
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-500" />
          </div>

          {showSuggestions && (
            <ul className="suggestions-list">
              {filteredSuggestions.map((suggestion) => (
                <li
                  key={suggestion.hpoid}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="suggestion-item"
                >
                  <span className="suggestion-name">{suggestion.name}</span>
                  <span className="suggestion-synonyms">
                    {suggestion.synonyms}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {selectedItems.map((item) => (
        <div key={item.hpoid} className="selected-item">
          <div className="selected-item-header">
            <Checkbox
              label={
                <span className="item-label">
                  {item.name}
                  <FaInfoCircle
                    className="info-icon cursor-pointer text-blue-500"
                    onClick={() => handleInfoClick(item)}
                  />
                </span>
              }
              checked={item.checked}
              onChange={() => handleTagToggle(item)}
            />

            {item.checked && (
              <div className="date-picker-card">
                <div className="date-picker-body">
                  <div className="date-text-container">
                    <p className="date-text">{formatDate(item.startDate)}</p>
                    <p className="date-text">{formatDate(item.endDate)}</p>
                    <FaCalendarAlt
                      className="calendar-icon"
                      onClick={() => handleCalendarClick(item)}
                    />
                  </div>

                  <h4 className="source-text-source">{item.source}</h4>
                </div>
              </div>
            )}
          </div>
          {item.synonyms && (
            <div className="item-synonyms">Synonyms: {item.synonyms}</div>
          )}
        </div>
      ))}
      {activeDatePopup && (
        <DatePopup
          item={activeDatePopup}
          onClose={handleCloseDatePopup}
          onDateChange={handleDateChange}
        />
      )}
      {/* Modal for displaying symptom details */}
      {selectedSymptom && (
        <SymptomDetailsPopup
          isOpen={isModalOpen}
          onClose={closeModal}
          selectedSymptom={selectedSymptom}
          contentLabel="Symptom Details"
        />
      )}
    </div>
  );
};

export default Symptoms;
